import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { isEqual } from "lodash";

import { useStateSelector } from "utils/field";

import { isEquation, parseEquation } from "utils/fieldEquation";
import convertUnit from "utils/convertUnit";
import { createInputFocusHandler, createInputKeyDownHandler } from "components/Input";

import mixpanel from "mixpanel-browser";
import LoadingDots from "components/LoadingDots";
import WarningTooltip from "components/WarningTooltip";
import NonH2kParamTooltip from "components/NonH2kParamTooltip";
import InfoTooltip from "components/InfoTooltip";
import Tooltip from "components/Tooltip";
import EquationTooltip from "../EquationTooltip";
import { change } from "store/root/actions";
import EditTooltip from "../EditTooltip";
import Units from "./Units";

import DrawingIcon from "assets/images/icons/JSX/Drawing";
import classes from "../style.module.scss";
import classNames from "classnames";

export const UNIT_SYSTEMS = ["Metric", "Imperial", "US"];

const UNIT_SUFFIX = "_u";
const EQUATION_SUFFIX = "_eqn";

// Equations are entered in a field's display units, so an equation attached to a
// field whose display unit is being reset would otherwise be left in the old system.
const hasUnitSibling = (obj, eqnKey) => `${eqnKey.slice(0, -EQUATION_SUFFIX.length)}${UNIT_SUFFIX}` in obj;

export const resetDisplayUnits = (data) => {
    if (Array.isArray(data)) {
        return data.map((item) => resetDisplayUnits(item));
    }

    if (!data || typeof data !== "object") {
        return data;
    }

    return Object.keys(data).reduce((acc, key) => {
        if (key.endsWith(UNIT_SUFFIX)) {
            acc[key] = "";
        } else if (key.endsWith(EQUATION_SUFFIX) && hasUnitSibling(data, key)) {
            acc[key] = "";
        } else {
            acc[key] = resetDisplayUnits(data[key]);
        }

        return acc;
    }, {});
};

//TODO: make tooltip more unique
const InputWithUnits = ({
    name: topLevelName = "",
    label,
    input = {},
    equationInput = {},
    meta = {},
    type,
    className,
    large,
    compact = false,
    highlight = false,
    isStat = false,
    transparent = false,
    placeholder = "",
    hideLabel,
    autoComplete = "off",
    info = "",
    nonH2kParam = "",
    disabled = false,
    parentError = false,
    quantity = false,
    setValue,
    decimals = 0,
    hideField,
    equation,
    units: { base: { trueBase = "m", displayBase = "m" } = {}, options = [], selected = "", unitType, accessor } = {},
    isDrawing = false,
    isDrawingTip = "",
    editToggle,
    withEquation = false,
    onUnitChange = () => "",
    isLoading = false,
    warning = "",
    reset = false,
    toggleReset = () => {},
    style,
    onBlurAction = () => {},
    tabIndex,
}) => {
    const dispatch = useDispatch();

    const [selectedUnit, changeSelectedUnit] = useState(selected || displayBase || trueBase);
    const [displayValue, changeDisplayValue] = useState("");
    const [focused, toggleFocused] = useState(false);

    const [equationValue, changeEquationValue] = useState("");
    const [invalidEquation, setInvalidEquation] = useState(false);

    const isInitialDisplayBase = useRef(true);

    const { invalid = false, error = "" } = meta;
    // 'value' here represents the trueValue
    const { value, onChange, name = "" } = input;

    // TODO: equation prop can be moved here as eqnValue, and equ_accessor as eqnName
    const { eqnValue, onEqnChange = () => {} } = equationInput;

    let equ_accessor = withEquation && name ? `${name}_eqn` : "";
    //If our accessor ends in .value, it's contained within an object and the select field tied to it may rely on that object structure, so go up one level
    equ_accessor = equ_accessor.includes(".value_eqn") ? equ_accessor.replace(".value_eqn", "_eqn") : equ_accessor;

    const checkValue = useStateSelector(topLevelName);

    const handleInputFocus = createInputFocusHandler({
        value,
        displayValues: [displayValue, equationValue],
        onClear: () => {
            changeDisplayValue("");
            changeEquationValue("");
        },
        onFocus: () => toggleFocused(true),
    });

    const handleInputKeyDown = createInputKeyDownHandler({
        onFocus: () => toggleFocused(true),
        focused,
    });

    const mainClasses = [
        classes.inputField,
        className && className,
        large && classes.large,
        compact && classes.compact,
        highlight && !disabled && classes.highlight,
        isStat && classes.smallStat,
        transparent && classes.transparent,
        quantity && classes.hasQuantity,
        (parentError || invalid) && classes.invalid,
        warning && classes.warning,
        hideField && classes.hideField,
    ].filter((c) => c);

    const handleSetValue = () => {
        if (isNaN(setValue)) {
            // Sometimes setValue is an object
            // Add decimals to field when display value is 0
            changeDisplayValue(parseFloat("0.00").toFixed(decimals));
        } else {
            const newDisplayValue = parseFloat(
                convertUnit({
                    value: parseFloat(setValue),
                    type: unitType,
                    inputUnit: trueBase,
                    outputUnit: selectedUnit,
                })
            ).toFixed(decimals);

            // Check if stored equation calculates to display value and not the true value
            const newEquationValue =
                equation && parseEquation(equation).toFixed(decimals) === newDisplayValue ? equation : newDisplayValue;

            changeDisplayValue(newDisplayValue);
            changeEquationValue(newEquationValue);

            if (checkValue !== null) onChange(parseFloat(parseFloat(setValue).toFixed(decimals + 2)));
        }
    };

    // Handles any change made to the display field
    const handleChange = (e) => {
        const newDisplayValue = parseFloat(e.target.value);
        changeDisplayValue(e.target.value);

        const newTrueValue = parseFloat(
            convertUnit({
                value: newDisplayValue,
                type: unitType,
                inputUnit: selectedUnit,
                outputUnit: trueBase,
            }).toFixed(decimals + 2)
        );

        onChange(newTrueValue);
    };

    // Handles any change made to the equation field
    const handleEqnChange = (e) => {
        var eqnInput = e.target.value.trimStart();
        let newDisplayValue = parseEquation(eqnInput);
        let isInvalid = newDisplayValue === null ? true : false;

        // If input does not start with "=" autofill it to mark the field as touched. Untouched equation fields should just be an integer.
        if (!isEquation(eqnInput)) {
            eqnInput = "=" + eqnInput;
        }

        setInvalidEquation(isInvalid);
        changeEquationValue(eqnInput);
        changeDisplayValue(newDisplayValue);

        // Save trueValue and equation value if equation is valid
        if (!isInvalid) {
            const newTrueValue = parseFloat(
                convertUnit({
                    value: newDisplayValue,
                    type: unitType,
                    inputUnit: selectedUnit,
                    outputUnit: trueBase,
                }).toFixed(decimals + 2)
            );

            onChange(newTrueValue);
            // Updates form so duplicate fields get updated equation
            if (equ_accessor) {
                dispatch(change(equ_accessor, eqnInput));
            }
            // Update equation in parent state not attached to form
            onEqnChange(eqnInput);
        }
    };

    const isEmptyOnBlur = () =>
        displayValue === "" || (withEquation && String(equationValue).trim() === "");

    // Handles what happens when the user leaves the display field
    const handleBlur = () => {
        onBlurAction();

        const newTrueValue = isEmptyOnBlur()
            ? 0
            : parseFloat(parseFloat(value || 0).toFixed(decimals + 2));
        const newDisplayValue = parseFloat(isEmptyOnBlur() ? 0 : displayValue || 0).toFixed(decimals);

        toggleFocused(false);
        onChange(newTrueValue);
        changeDisplayValue(newDisplayValue);

        if (setValue || setValue === 0) {
            handleSetValue();
        }
    };

    // Handles what happens when the user leaves the equation field
    const handleEqnBlur = () => {
        handleBlur();

        if (equationValue === "") {
            // If equation cleared and field exited, set equation value to 0
            changeEquationValue(parseFloat("0.00").toFixed(decimals));
        } else if (!isEquation(equationValue) && !isNaN(equationValue)) {
            // If number entered, autofill decimal places before saving
            changeEquationValue(parseFloat(equationValue).toFixed(decimals));
        }
    };

    // Handles a new unit selection
    const handleUnitChange = (unit) => {
        const newDisplayValue = parseFloat(
            convertUnit({
                value: value, //Always using value here so we don't lose decimals
                type: unitType,
                inputUnit: trueBase,
                outputUnit: unit,
            })
        ).toFixed(decimals);

        changeDisplayValue(parseFloat(newDisplayValue));
        changeSelectedUnit(unit);

        // Reset equation to display value
        if (withEquation && equ_accessor) {
            changeEquationValue(newDisplayValue);
            dispatch(change(equ_accessor, newDisplayValue));
        }

        // Changes the unit field in the form
        if (accessor) {
            dispatch(change(accessor, unit));
        }
        onUnitChange(unit);
    };

    useEffect(() => {
        // Get the right display value when component is initialized
        const newDisplayValue = parseFloat(
            convertUnit({
                value: value,
                type: unitType,
                inputUnit: trueBase,
                outputUnit: selectedUnit,
            })
        ).toFixed(decimals);

        if (value || value === 0) {
            onChange(parseFloat(value));
            changeDisplayValue(newDisplayValue);
            changeEquationValue(equation ? equation : newDisplayValue >= 0 ? newDisplayValue : ""); // assuming all values are positive
        }
    }, []);

    useEffect(() => {
        if (reset) {
            changeDisplayValue("");
            changeEquationValue("");
            toggleReset(false);
        }
    }, [reset]);

    // Updates unit if change from duplicate field updates the selected unit
    useEffect(() => {
        if (selected && !isEqual(selectedUnit, selected)) {
            handleUnitChange(selected);
        }
    }, [selected]);

    // Follows the model's unit system when it is switched globally
    useEffect(() => {
        if (isInitialDisplayBase.current) {
            isInitialDisplayBase.current = false;
            return;
        }

        if (displayBase && !isEqual(selectedUnit, displayBase)) {
            handleUnitChange(displayBase);
        }
    }, [displayBase]);

    // why goes into infinite loop?
    useEffect(() => {
        // If value is manually set, do stuff
        if ((setValue || setValue === 0) && !focused) {
            handleSetValue();
        }
    }, [setValue]);

    const errorTooltipProps =
        transparent && error && invalid
            ? {
                  [`data-html`]: true,
                  [`data-tip`]: `<span style="display:block;max-width:14rem;">${error}</span>`,
                  [`data-for`]: `${name}_hover_error`,
              }
            : {};

    return (
        <div className={`${mainClasses.join(" ")} ${invalidEquation && classes.invalid}`} style={style}>
            <div className={classes.fieldHeader}>
                <label
                    className={classNames({ ["invisible"]: hideLabel, [classes.isDrawing]: isDrawing })}
                    htmlFor={name}
                >
                    {label}{" "}
                    {isDrawing && (
                        <>
                            <span
                                data-html
                                data-for={`${name}_drawingTip`}
                                data-tip={`<span style="display:block;max-width:10.5rem;text-align:center;">${
                                    isDrawingTip
                                        ? isDrawingTip
                                        : "This field can only be edited in the <strong>Drawing Capture</strong> tool."
                                }</span>`}
                            >
                                <DrawingIcon />
                            </span>
                            <Tooltip id={`${name}_drawingTip`} />
                        </>
                    )}
                </label>
                <div className={classes.toolTipList}>
                    {info && !isStat && <InfoTooltip className={classes.infoTooltip} info={info} />}
                    {warning && !isStat && (
                        <WarningTooltip
                            className={classNames(classes.warningTooltip, { [classes.pushLeft]: editToggle || info })}
                            warning={warning}
                        />
                    )}
                    {nonH2kParam && !isStat && (
                        <NonH2kParamTooltip
                            className={classNames(classes.nonH2kParamTooltip, { [classes.pushLeft]: warning })}
                            nonH2kParam={nonH2kParam}
                        />
                    )}
                    {editToggle && !isStat && (
                        <EditTooltip
                            className={classes.editTooltip}
                            onClick={() => {
                                editToggle();
                                mixpanel.track("Summary Calculation Open", {
                                    "Field Name": label,
                                });
                            }}
                            name={name}
                            label={label}
                        />
                    )}
                    {withEquation && !disabled && !isStat && !transparent && (
                        <EquationTooltip
                            className={classNames(classes.equationTooltip, {
                                [classes.pushLeft]: info || warning || nonH2kParam,
                                [classes.pushFarLeft]: (info && (warning || nonH2kParam)) || (warning && nonH2kParam),
                            })}
                            name={name}
                            hasEquation={isEquation(equationValue)}
                            isInvalid={invalidEquation}
                        />
                    )}
                </div>
            </div>
            {isLoading && (
                <div className={classes.loading}>
                    <LoadingDots />
                </div>
            )}
            {!isLoading && (
                <div className={classes.inputWithUnit} {...errorTooltipProps}>
                    {!withEquation && (
                        <input
                            id={`${name}-front`}
                            name={`${name}-front`}
                            type={type}
                            placeholder={placeholder}
                            autoComplete={autoComplete}
                            disabled={disabled}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            onFocus={handleInputFocus}
                            onKeyDown={handleInputKeyDown}
                            tabIndex={tabIndex}
                            value={displayValue}
                        />
                    )}
                    {withEquation && (
                        <input
                            id={`${name}-eqn`}
                            name={`${name}-eqn`}
                            type="text"
                            placeholder={
                                (focused || invalidEquation) && !transparent ? "Enter equation or value" : placeholder
                            }
                            disabled={disabled}
                            onChange={handleEqnChange}
                            onBlur={handleEqnBlur}
                            onFocus={handleInputFocus}
                            onKeyDown={handleInputKeyDown}
                            tabIndex={tabIndex}
                            value={focused || invalidEquation ? equationValue : displayValue}
                        />
                    )}

                    <input {...input} id={name} name={name} type="hidden" />
                    <Units
                        selectedUnit={selectedUnit}
                        unitOptions={options}
                        changeSelected={handleUnitChange}
                        highlight={highlight}
                    />
                </div>
            )}
            {transparent && invalid && error && <Tooltip id={`${name}_hover_error`} />}
            {!invalidEquation && !isStat && !transparent && invalid && error && (
                <span className={classes.errorMessage}>{error}</span>
            )}
            {invalidEquation && <span className={classes.errorMessage}>Invalid equation entered</span>}
        </div>
    );
};

export default InputWithUnits;
